// ============================================================================
// UTILITÁRIO: browserEnv — Abstração segura do window/globalThis para SSR
// ARQUIVO: src/utils/browserEnv.js
// RESPONSABILIDADE: Todos os acessos ao ambiente do navegador devem passar aqui.
//   Nenhum componente deve chamar window.* diretamente.
// ============================================================================

const IS_BROWSER = typeof window !== 'undefined';

export const browserEnv = {
  exists: () => IS_BROWSER,

  // LocalStorage com fallback silencioso
  localStorage: {
    getItem: (key) => {
      if (!IS_BROWSER) return null;
      try { return window.localStorage.getItem(key); } catch { return null; }
    },
    setItem: (key, value) => {
      if (!IS_BROWSER) return;
      try { window.localStorage.setItem(key, value); } catch { /* ignore */ }
    },
    removeItem: (key) => {
      if (!IS_BROWSER) return;
      try { window.localStorage.removeItem(key); } catch { /* ignore */ }
    },
  },

  // Location (leitura segura)
  location: {
    get href()    { return IS_BROWSER ? window.location.href : ''; },
    get origin()  { return IS_BROWSER ? window.location.origin : ''; },
    get pathname(){ return IS_BROWSER ? window.location.pathname : ''; },
    get search()  { return IS_BROWSER ? window.location.search : ''; },
    get hash()    { return IS_BROWSER ? window.location.hash : ''; },
    get hostname(){ return IS_BROWSER ? window.location.hostname : ''; },
    reload: () => { if (IS_BROWSER) window.location.reload(); },
  },

  // Eventos customizados
  dispatchEvent: (event) => {
    if (!IS_BROWSER) return;
    window.dispatchEvent(event);
  },
  addEventListener: (type, handler, options) => {
    if (!IS_BROWSER) return () => {};
    window.addEventListener(type, handler, options);
    return () => window.removeEventListener(type, handler, options);
  },

  // Debug helpers (DEV only)
  setDebug: (key, value) => {
    if (!IS_BROWSER || !import.meta.env?.DEV) return;
    window.__DEBUG = window.__DEBUG || {};
    window.__DEBUG[key] = value;
  },

  // Timers (usar globais — window.* é redundante)
  setTimeout: (fn, ms) => (IS_BROWSER ? setTimeout(fn, ms) : 0),
  clearTimeout: (id) => { if (IS_BROWSER) clearTimeout(id); },

  // Scroll
  scrollTo: (options) => {
    if (IS_BROWSER) window.scrollTo(options);
  },

  // Open (links externos)
  open: (url, target = '_blank', features = 'noopener,noreferrer') => {
    if (IS_BROWSER) window.open(url, target, features);
  },
};

export default browserEnv;
